import React, {
  useCallback, useEffect, useReducer, useState,
} from 'react';
import { Link, useHistory, useLocation } from 'react-router-dom';
import Grid from '@mui/material/Grid';
import { Button, Divider, Stack } from '@mui/material';
import { dateTimeString, truncateString } from 'shared/utils';
import { BUTTON_COLORS, VARIANTS } from 'shared/constants';
import { useRequest } from 'shared/hooks';
import { TextEditor } from 'shared/components/data-display/editors';
import { InfoModal, Title } from 'shared/components/abstract/interactables';
import { AsyncSearchableSelect } from 'shared/components/select/SearchableSelects';
import {
  createTag,
  deleteTag,
  getTag,
  searchTags,
  searchTickets,
  updateTag,
} from 'shared/api/support';
import DataTable from 'shared/components/table/DataTable';
import { dataReducer } from 'shared/reducers';

const initialState = {
  data: null,
  updatedData: {},
  dirty: false,
  newName: '',
};

function reducer(state, { action, payload }) {
  switch (action) {
    case 'onSave':
      return {
        ...state,
        dirty: false,
        updatedData: {},
        data: payload,
      };
    case 'setInitialState':
      return {
        ...payload,
      };
    case 'createTag':
      return {
        ...state,
        dirty: false,
        updatedData: {},
        data: {
          ...payload,
          staged: false,
        },
      };
    case 'setResourceStaged':
      return {
        ...state,
        data: { ...payload, name: payload.name },
      };
    case 'setResourceUnstaged':
      return {
        ...state,
        data: payload,
      };
    case 'stageItem':
      return {
        ...state,
        data: { name: payload, staged: true },
      };
    default:
      return dataReducer(state, { action, payload });
  }
}

const ticketColumns = [
  {
    name: 'Ticket #',
    orderBy: 'id',
    formatter: (row) => <Link to={`/ticket/view/${row.id}`}>{row.id}</Link>,
  },
  {
    name: 'Title',
    jsonKey: 'title',
    formatter: (row) => <Link to={`/ticket/view/${row.id}`}>{truncateString(row.title, 56)}</Link>,
  },
  {
    name: 'Building',
    jsonKey: 'building',
    formatter: (row) => (row.ticket_association?.building && <div className="small-line-clamp">{row.ticket_association.building.name}</div>),
  },
  {
    name: 'Created At',
    orderBy: 'created_at',
    formatter: (row) => dateTimeString(row.created_at),
  },
];

export default function TicketTag() {
  const { request, isLoading } = useRequest();
  const { push: pushHistory, replace: replaceHistory } = useHistory();
  const { search } = useLocation();
  const [state, dispatch] = useReducer(reducer, initialState);
  const [updateCount, setUpdateCount] = useState(0);
  const [disableDelete, setDisableDelete] = useState(false);
  const forceUpdate = () => setUpdateCount(updateCount + 1);
  const { data: tag, updatedData } = state;

  // syncs the id in the url to the tag
  useEffect(() => {
    const id = parseInt(new URLSearchParams(search).get('id'), 10);
    // if reloading after a change and the tag has not changed then return
    if (tag && (tag.id === id || tag.staged)) {
      return;
    }
    if (!id) {
      dispatch({ action: 'setInitialState', payload: initialState });
      replaceHistory('?');
      return;
    }
    (async () => {
      if (id) {
        const result = await request(getTag(id));
        if (!result) {
          pushHistory('?');
        } else {
          dispatch({ action: 'set', payload: { ...result } });
        }
      }
    })();
  }, [request, search, tag, updateCount, pushHistory, replaceHistory]);

  useEffect(() => {
    (async () => {
      if (tag?.id) {
        const result = await request(searchTickets(`?tag_id=${tag.id}&with_association=true`, 'application/json'));
        if (result) {
          if (result.rows.length > 0) {
            setDisableDelete(true);
          } else {
            setDisableDelete(false);
          }
        }
      }
    })();
  }, [request, tag?.id]);

  const setResource = useCallback((newTag) => {
    if (!newTag) {
      dispatch({ action: 'setInitialState', initialState });
      pushHistory('?');
    } else {
      // sets the new resource to the current state and pushes history
      // when creating a new tag, set the name to the name given
      if (newTag.staged) {
        dispatch({ action: 'setResourceStaged', payload: newTag });
      } else {
        dispatch({ action: 'setResourceUnstaged', payload: newTag });
      }
      pushHistory(newTag.id === undefined ? '?' : `?id=${newTag.id}`);
    }
  }, [pushHistory]);

  const onDelete = async () => {
    const result = await request(deleteTag(tag.id), 'Deleted tag');
    if (result) {
      dispatch({ action: 'setInitialState', payload: initialState });
      forceUpdate();
      pushHistory('?');
    }
  };

  const setTagName = (value) => dispatch({ action: 'update', payload: { key: 'name', value } });

  const onSave = async () => {
    if (Object.keys(updatedData).length > 0) {
      let newData = { ...tag, ...updatedData };
      newData = { ...newData };
      const result = await request(updateTag(tag.id, newData), 'Updated');
      if (result) {
        dispatch({ action: 'onSave', payload: result });
        forceUpdate();
      }
    }
    return null;
  };

  const onClear = () => {
    dispatch({ action: 'clear' });
  };

  const onCreate = async () => {
    let newData = { ...tag, ...updatedData };
    newData = { ...newData };
    const result = await request(createTag(newData), 'Created New Tag');
    if (!result) { return; }
    pushHistory(`?id=${result.id}`);
    dispatch({ action: 'createTag', payload: result });
    forceUpdate();
  };

  const stageItem = (name) => {
    dispatch({ action: 'stageItem', payload: name });
  };

  return (
    <Grid container spacing={1}>
      <Title title="Tags" />
      <Grid
        size={{
          xs: 12, md: 6, lg: 4, xl: 3,
        }}
      >
        <AsyncSearchableSelect
          creatable
          label="Tag Search"
          onChange={setResource}
          onCreate={stageItem}
          searchRequest={searchTags}
          selectedOption={tag}
          getOptionLabel={(r) => r.name}
          updateCount={updateCount}
        />
      </Grid>
      <Grid size={12}>
        <Divider />
      </Grid>
      {tag
        && (
          <Grid container spacing={2} sx={{ justifyContent: 'flex-start' }}>
            <Grid size={{
              xs: 12, md: 6, lg: 4, xl: 3,
            }}
            >
              <TextEditor label="Name" value={updatedData.name || tag.name} setValue={setTagName} />
            </Grid>
            <Grid sx={{ my: (theme) => theme.spacing(1) }}>
              {state.dirty && !tag.staged
                  && (
                  <Stack direction="row" spacing={1}>
                    <Button
                      disabled={isLoading}
                      onClick={onClear}
                      variant={VARIANTS.outlined}
                    >
                      Undo
                    </Button>
                    <InfoModal
                      margin={(theme) => theme.spacing(0, 1)}
                      title="Save Tag"
                      actionName="Save"
                      description={<p>{`This will update "${tag.name}". Are you sure?`}</p>}
                      actionCallback={onSave}
                      color={BUTTON_COLORS.primary}
                      disabled={isLoading}
                    />
                  </Stack>
                  )}
              {!state.dirty && !tag.staged && (
              <InfoModal
                title="Delete Tag"
                actionName="Delete Tag"
                description={<p>{`This will delete "${tag.name}". Are you sure?`}</p>}
                actionCallback={onDelete}
                color={BUTTON_COLORS.error}
                disabled={isLoading || disableDelete}
                disabledTip="Cannot delete a tag with existing tickets"
              />
              )}
              {tag.staged && (
              <Stack direction="row" sx={{ justifyContent: 'flex-end' }}>
                <InfoModal
                  title="Create Tag"
                  actionName="Create"
                  description={<p>{`This will create a new tag  "${updatedData.name || tag.name}". Are you sure?`}</p>}
                  actionCallback={onCreate}
                  color={BUTTON_COLORS.primary}
                  disabled={isLoading || (tag.name === '')}
                />
              </Stack>
              )}
            </Grid>
            <Grid
              size={12}
              sx={{ marginTop: '10px', width: '100%' }}
            >
              {!tag.staged && (
                <DataTable
                  tableName="Tagged Tickets"
                  className="tag-tickets-table-height"
                  columns={ticketColumns}
                  baseSearch={`tag_id=${tag.id}&with_association=true`}
                  searchRequest={searchTickets}
                  rowId="id"
                  noSearchForm
                  noExportButton
                  noRowsText="No matching tickets"
                />
              )}
            </Grid>
          </Grid>
        )}
    </Grid>
  );
}
